import { DataStore } from 'aws-amplify';
import { SSCC } from '../../models';
import dayjs from 'dayjs';

/**
 * Cleanup duplicate SSCC records from DataStore
 * This function identifies duplicates by ID and removes older versions
 */
export async function cleanupDuplicateSSCCs(): Promise<void> {
  try {
    console.log('Starting DataStore duplicate cleanup...');

    // Get all SSCCs from DataStore
    const allSSCCs = await DataStore.query(SSCC);
    console.log(`Found ${allSSCCs.length} total SSCCs in DataStore`);

    // Group by ID to find duplicates
    const ssccGroups = new Map<string, SSCC[]>();

    allSSCCs.forEach((sscc) => {
      const existing = ssccGroups.get(sscc.id);
      if (existing) {
        existing.push(sscc);
      } else {
        ssccGroups.set(sscc.id, [sscc]);
      }
    });

    // Find groups with duplicates
    const duplicateGroups = Array.from(ssccGroups.entries()).filter(([_, ssccs]) => ssccs.length > 1);

    if (duplicateGroups.length === 0) {
      console.log('No duplicate SSCCs found');
      return;
    }

    console.log(`Found ${duplicateGroups.length} SSCCs with duplicates`);

    // For each duplicate group, keep the most recent and delete the rest
    for (const [ssccId, duplicates] of duplicateGroups) {
      console.log(`Processing duplicates for SSCC ${ssccId}: ${duplicates.length} copies found`);

      // Sort by updatedAt (most recent first) or createdAt as fallback
      const sortedDuplicates = duplicates.sort((a, b) => {
        const timeA = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const timeB = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return timeB - timeA; // Most recent first
      });

      // Keep the first (most recent) and delete the rest
      const toKeep = sortedDuplicates[0];
      const toDelete = sortedDuplicates.slice(1);

      console.log(`Keeping SSCC ${ssccId} with updatedAt: ${toKeep.updatedAt}`);

      // Delete the older duplicates
      for (const duplicate of toDelete) {
        try {
          await DataStore.delete(duplicate);
          console.log(`Deleted duplicate SSCC ${ssccId} with updatedAt: ${duplicate.updatedAt}`);
        } catch (error) {
          console.error(`Failed to delete duplicate SSCC ${ssccId}:`, error);
        }
      }
    }

    console.log('DataStore duplicate cleanup completed');
  } catch (error) {
    console.error('Error during DataStore cleanup:', error);
  }
}

/**
 * Check if there are duplicate SSCCs in DataStore
 */
export async function checkForDuplicateSSCCs(): Promise<boolean> {
  try {
    const allSSCCs = await DataStore.query(SSCC);
    const uniqueIds = new Set(allSSCCs.map((sscc) => sscc.id));

    const hasDuplicates = uniqueIds.size !== allSSCCs.length;

    if (hasDuplicates) {
      console.warn(`Found ${allSSCCs.length - uniqueIds.size} duplicate SSCCs in DataStore`);
    }

    return hasDuplicates;
  } catch (error) {
    console.error('Error checking for duplicates:', error);
    return false;
  }
}
